import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";

const MPA_TERMS_PDF_URL = `${process.env.REACT_APP_ASSET_ENDPOINT}/documents/mpa-slz-permit-terms-and-conditions.pdf`;

export interface TermsConditionsSectionProps {
  authorityId: string;
  termsAcceptedAt: string | null;
  onTermsChange: (acceptedAt: string | null) => void;
}

export const TermsConditionsSection: React.FC<TermsConditionsSectionProps> = ({
  authorityId,
  termsAcceptedAt,
  onTermsChange,
}) => {
  const isAccepted = termsAcceptedAt !== null;
  const isMPA = authorityId && authorityId === process.env.REACT_APP_MPA_ACCOUNT_ID;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTermsChange(event.target.checked ? new Date().toISOString() : null);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h3" gutterBottom>
          Terms & Conditions
        </Typography>

        <Box sx={{ mb: 2 }}>
          {isMPA ? (
            <Typography variant="body2" color="text.secondary">
              By submitting this application, you agree to the{" "}
              <Link href={MPA_TERMS_PDF_URL} target="_blank" rel="noopener noreferrer">
                terms and conditions
              </Link>
              .
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                By submitting this application, you agree to the following terms and conditions:
              </Typography>

              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  All information provided in this application is accurate and complete.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  You understand that providing false information may result in permit denial or revocation.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  You agree to comply with all applicable parking regulations while using this permit.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  The issuing authority reserves the right to suspend or revoke permits for violations.
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Permits are non-transferable and must be used only for the registered vehicles.
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <FormControlLabel
          control={<Checkbox checked={isAccepted} onChange={handleChange} color="primary" />}
          label={
            <Typography variant="body2" fontWeight={500}>
              I have read and agree to the terms and conditions
            </Typography>
          }
          sx={{
            alignItems: "flex-start",
            "& .MuiCheckbox-root": { pt: 0 },
          }}
        />
      </CardContent>
    </Card>
  );
};

export default TermsConditionsSection;
